import { Box, createListCollection, Field as ChakraField, HStack, Input, Text, VStack } from '@chakra-ui/react';
import ErrorContainer from '@components/common/form/ErrorContainer';
import CustomChipList from '@components/common/inputs/CustomChipList';
import { MapCustomScrollbar } from '@components/common/layout/MapCustomScrollbar';
import { InputNumber } from '@components/ui/input/InputNumber';
import { CustomSelect } from '@components/ui/select/Select';
import { Switch } from '@components/ui/switch';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveGroup } from '@redux/authent/authent.selectors';
import { selectActiveGroupsPlatforms, selectUsableProcedureModels } from '@redux/config/config.selectors';
import { isNoFlyZoneType } from '@utils/map/zone.utils';
import { Field, FormikErrors, FormikTouched } from 'formik';
import { Dispatch, SetStateAction } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { CoordinatesUnitEnum, Mark, MarkEnum } from '@/types/commons/commons.types';
import { Zone2DTypeEnum, zoneLabelFromZone2DType } from '@/types/config/config.types';
import { ZoneFormDisplayedFormatEnum } from '@/types/map.types';

import { OldZoneForm } from './ZoneFormPopup';

type Props = {
  touched: FormikTouched<OldZoneForm>;
  errors: FormikErrors<OldZoneForm>;
  values: OldZoneForm;
  isZoneTypeDisabled?: boolean;
  unit: CoordinatesUnitEnum;
  setUnit: Dispatch<SetStateAction<CoordinatesUnitEnum>>;
  handleHeightToAltitudeChange: () => void;
  setFieldValue: (
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    shouldValidate?: boolean | undefined,
  ) => Promise<void | FormikErrors<OldZoneForm>>;
  setFieldTouched: (
    field: string,
    isTouched?: boolean | undefined,
    shouldValidate?: boolean | undefined,
  ) => Promise<void | FormikErrors<OldZoneForm>>;
};

function addSelectedPlatformToMarks(selected: string | null, actual: Mark[]): Mark[] {
  if (selected !== null && actual.some((mark) => mark.type === MarkEnum.PLATFORM && mark.context === selected)) {
    return actual;
  }
  const withoutPlatforms = actual.filter((mark) => mark.type !== MarkEnum.PLATFORM);
  if (selected === null) {
    return withoutPlatforms;
  }

  return [
    ...withoutPlatforms,
    {
      id: null,
      type: MarkEnum.PLATFORM,
      context: selected,
    } as Mark,
  ];
}

export default function ZoneDetailsForm({
  touched,
  errors,
  values,
  isZoneTypeDisabled,
  unit,
  setUnit,
  handleHeightToAltitudeChange,
  setFieldTouched,
  setFieldValue,
}: Readonly<Props>) {
  const { formatMessage } = useIntl();

  const usableProcedureModels = useAppSelector(selectUsableProcedureModels);
  const activeGroup = useAppSelector(selectActiveGroup);
  const usablePlatforms = useAppSelector(selectActiveGroupsPlatforms);
  const defaultPlatform = activeGroup?.defaultPlatform ?? undefined;
  const currentMarks = values.marks ?? [];

  const chipOptions = Object.values(ZoneFormDisplayedFormatEnum).map((key) => ({
    label: formatMessage({ id: `zone.${key}` }),
    value: key,
  }));

  function handleUnitChange(unit: string) {
    if (unit === CoordinatesUnitEnum.DECIMAL_DEGREES || unit === CoordinatesUnitEnum.DMS) {
      setUnit(unit);
    }
  }

  return (
    <Box height="100%" width="360px" flexShrink={0} backgroundColor="neutral.700">
      <MapCustomScrollbar height="100%" thumbColor="neutral.500" marginTop={1} marginBottom={1} margin={1}>
        <VStack height="100%" width="100%" padding={3} paddingRight={3} gap={3} alignItems="start">
          <ChakraField.Root required invalid={touched.zoneName && !!errors.zoneName}>
            <VStack width="100%" gap={1} alignItems="start">
              <ChakraField.Label>
                <Text color="neutral.300" fontWeight="medium" textStyle="md" pointerEvents="none">
                  <FormattedMessage id="zone.name" />
                </Text>
              </ChakraField.Label>
              <Field
                as={Input}
                id="zoneName"
                placeholder={formatMessage({ id: 'zone.name' })}
                name="zoneName"
                height="48px"
                width="312px"
                type="text"
                invalid={touched.zoneName && !!errors.zoneName}
              />
              <ChakraField.ErrorText>
                <ErrorContainer error={errors.zoneName} touched={touched.zoneName} />
              </ChakraField.ErrorText>
            </VStack>
          </ChakraField.Root>
          <ChakraField.Root id="zoneType" required invalid={!!errors.zoneType}>
            <VStack width="100%" gap={1} alignItems="start">
              <Text color="neutral.300" pointerEvents="none">
                <FormattedMessage id="zone.type" />
              </Text>
              <CustomSelect
                disabled={isZoneTypeDisabled}
                variant="darker"
                width="312px"
                size="sm"
                collection={createListCollection({
                  items: Object.values(Zone2DTypeEnum).map((value) => ({
                    label: formatMessage({ id: `filters.${zoneLabelFromZone2DType[value]}` }),
                    value: value as string,
                  })),
                })}
                onChange={(value) => setFieldValue('zoneType', value)}
                value={values.zoneType}
              />
              <ErrorContainer error={errors.zoneType} touched={touched.zoneType} />
            </VStack>
          </ChakraField.Root>
          {values.zoneType && isNoFlyZoneType(values.zoneType) && (
            <>
              <ChakraField.Root id="marks" required invalid={!!errors.marks}>
                <VStack width="100%" gap={1} alignItems="start">
                  <Text color="neutral.300" pointerEvents="none">
                    <FormattedMessage id="zone.platform" />
                  </Text>
                  <CustomSelect
                    variant="darker"
                    width="312px"
                    size="sm"
                    placeholder={formatMessage({ id: 'zone.noPlatform' })}
                    collection={createListCollection({
                      items: usablePlatforms.map((platform) => ({
                        label: platform.name,
                        value: platform.code,
                      })),
                    })}
                    onChange={(value) => setFieldValue('marks', addSelectedPlatformToMarks(value, currentMarks))}
                    value={values.marks?.find((mark) => mark.type === MarkEnum.PLATFORM)?.context ?? defaultPlatform}
                  />
                  <ErrorContainer error={errors.procedureModel} touched={touched.procedureModel} />
                </VStack>
              </ChakraField.Root>
              <ChakraField.Root id="procedureModel" required invalid={!!errors.procedureModel}>
                <VStack width="100%" gap={1} alignItems="start">
                  <Text color="neutral.300" pointerEvents="none">
                    <FormattedMessage id="zone.procedureModel" />
                  </Text>
                  <CustomSelect
                    variant="darker"
                    width="312px"
                    size="sm"
                    placeholder={formatMessage({ id: 'zone.noProcedureModel' })}
                    collection={createListCollection({
                      items: usableProcedureModels.map((procedureModel) => ({
                        label: procedureModel.name,
                        value: procedureModel.id.toString(),
                      })),
                    })}
                    onChange={(value) =>
                      setFieldValue(
                        'procedureModel',
                        usableProcedureModels.find((procedureModel) => procedureModel.id.toString() === value),
                      )
                    }
                    value={values.procedureModel?.id.toString()}
                  />
                  <ErrorContainer error={errors.procedureModel} touched={touched.procedureModel} />
                </VStack>
              </ChakraField.Root>
            </>
          )}
          {values.zoneType === Zone2DTypeEnum.IMZ && (
            <ChakraField.Root required>
              <VStack width="100%" gap={1} alignItems="start">
                <Text color="neutral.300" fontWeight="medium" textStyle="md" pointerEvents="none">
                  <FormattedMessage id="zone.meter" values={{ label: formatMessage({ id: 'zone.marginSize' }) }} />
                </Text>
                <Field
                  as={InputNumber}
                  id="marginSize"
                  name="marginSize"
                  width="64px"
                  height="48px"
                  min={0}
                  invalid={touched.marginSize && !!errors.marginSize}
                  onChange={async (value?: number) => {
                    await setFieldTouched('marginSize', true);
                    await setFieldValue('marginSize', value ?? '');
                  }}
                />
                <ErrorContainer error={errors.marginSize} touched={touched.marginSize} />
              </VStack>
            </ChakraField.Root>
          )}
          <VStack width="100%" gap={1} alignItems="start">
            <Text color="neutral.300" pointerEvents="none">
              <FormattedMessage id="zone.format" />
            </Text>
            <CustomSelect
              value={unit}
              width={136}
              size="sm"
              collection={createListCollection({
                items: Object.values(CoordinatesUnitEnum).map((key) => ({
                  label: formatMessage({ id: `common.units.${key}` }),
                  value: key as string,
                })),
              })}
              onChange={(value) => value && handleUnitChange(value)}
            />
          </VStack>
          <CustomChipList
            availableOptions={chipOptions}
            selectedOptions={chipOptions.filter((option) => option.value === values.displayedFormat)}
            distinctValues={[ZoneFormDisplayedFormatEnum.ALTITUDE, ZoneFormDisplayedFormatEnum.HEIGHT]}
            onChange={(value) => value[0] !== values.displayedFormat && handleHeightToAltitudeChange()}
          />
          <HStack width="100%" gap={2} alignItems="center">
            <Switch
              checked={values.isZoneActive}
              size="md"
              onCheckedChange={() => {
                setFieldValue('isZoneActive', !values.isZoneActive);
              }}
            />
            <Text pointerEvents="none">
              <FormattedMessage id="zone.activate" />
            </Text>
          </HStack>
          <HStack width="100%" gap={2} alignItems="center">
            <Switch checked={true} size="md" disabled />
            <Text color="neutral.300" pointerEvents="none">
              <FormattedMessage id="zone.constantHeights" />
            </Text>
          </HStack>
        </VStack>
      </MapCustomScrollbar>
    </Box>
  );
}
